import { useEffect, useState } from 'react';
import { useIsFetching } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api/client';
import { cn } from '@/lib/cn';
import { relativeTime } from '@/lib/format';

/**
 * The status line — a terminal-style footer, 24px tall, always present.
 *
 * This is the console's signature. It answers, without being asked: is the
 * server alive, is anything loading right now, what shortcuts apply here, and
 * what version am I looking at. Operators stop guessing whether the app is
 * stuck, which is most of what "feels fast" actually means.
 */
export function StatusLine() {
  const fetching = useIsFetching();
  const hints = useShortcutHints();

  /**
   * Liveness is probed with `/api/version`, not the API's `/health`.
   *
   * `/health` is mounted at the *server root*, and nothing routes the root to
   * the API: the vite dev proxy only forwards `/api`, and in production Caddy
   * sends `/api/*` to the API container and everything else to this one. So a
   * `/health` probe from the browser can only ever hit the front-end and fail,
   * which would light up "API unreachable" on a perfectly healthy store.
   * `/api/version` is unauthenticated, cheap, and reachable everywhere.
   */
  const {
    data: version,
    isError,
    dataUpdatedAt,
  } = useQuery({
    queryKey: ['version'],
    queryFn: () => api.get<{ version: string; buildSha: string | null }>('/version'),
    refetchInterval: 30_000,
    staleTime: 25_000,
    retry: 1,
  });
  const health = version
    ? { timestamp: new Date(dataUpdatedAt).toISOString() }
    : undefined;

  const online = useOnline();
  const state = !online
    ? { tone: 'negative', label: 'Offline' }
    : isError
      ? { tone: 'negative', label: 'API unreachable' }
      : health
        ? { tone: 'positive', label: 'Connected' }
        : { tone: 'neutral', label: 'Connecting…' };

  return (
    <footer className="h-statusline border-line bg-surface text-2xs text-ink-faint flex shrink-0 items-center gap-3 border-t px-3 font-mono">
      {/* Connection lamp */}
      <span className="flex items-center gap-1.5" title={health?.timestamp ?? ''}>
        <span
          className={cn(
            'size-1.5 rounded-full',
            state.tone === 'positive' && 'bg-positive',
            state.tone === 'negative' &&
              'bg-negative animate-[pulse-signal_1.4s_ease-in-out_infinite]',
            state.tone === 'neutral' && 'bg-ink-faint'
          )}
        />
        <span className={cn(state.tone === 'negative' && 'text-negative')}>
          {state.label}
        </span>
      </span>

      <span className="text-line-strong">│</span>

      {/* Live request indicator — the honest "is it working" signal. */}
      <span
        className={cn('tabular-nums', fetching > 0 ? 'text-signal-ink' : 'opacity-40')}
      >
        {fetching > 0 ? `⟳ ${fetching} loading` : 'idle'}
      </span>

      {/* Contextual shortcut hints. */}
      <div className="ml-auto flex min-w-0 items-center gap-3 overflow-hidden">
        {hints.map((h) => (
          <span key={h.keys} className="flex shrink-0 items-center gap-1">
            <kbd className="border-line bg-surface-sunken text-ink-muted rounded-xs border px-1">
              {h.keys}
            </kbd>
            <span className="hidden sm:inline">{h.label}</span>
          </span>
        ))}
      </div>

      <span className="text-line-strong">│</span>
      <span className="shrink-0 opacity-60" title="Server version">
        v{version?.version ?? '—'}
      </span>
      {health ? (
        <span className="hidden shrink-0 opacity-40 xl:inline">
          {relativeTime(health.timestamp)}
        </span>
      ) : null}
    </footer>
  );
}

/** Shortcuts that apply on the current screen. Screens publish their own via
 *  `useShortcutHint`, and these globals are always present. */
const GLOBAL_HINTS = [
  { keys: '⌘K', label: 'Commands' },
  { keys: '/', label: 'Search' },
  { keys: '?', label: 'Keys' },
];

type Hint = { keys: string; label: string };

let extraHints: Hint[] = [];
const hintListeners = new Set<(h: Hint[]) => void>();

/** Publish screen-specific hints to the status line for as long as it is mounted. */
export function publishHints(hints: Hint[]) {
  extraHints = hints;
  for (const l of hintListeners) l(extraHints);
}

function useShortcutHints(): Hint[] {
  const [extra, setExtra] = useState<Hint[]>(extraHints);
  useEffect(() => {
    hintListeners.add(setExtra);
    // Screens publish from their own mount effect, which runs *before* this one
    // (they are deeper in the tree). Without this resync the first publish
    // after a page load is broadcast to an empty listener set and the hints
    // never appear at all.
    setExtra(extraHints);
    return () => {
      hintListeners.delete(setExtra);
    };
  }, []);
  return mergeHints(extra);
}

/** A screen hint replaces the global hint for the same keys: on a screen that
 *  takes over "/", the global "Search" hint would be wrong, and two hints with
 *  one key collide in the list. */
export function mergeHints(extra: Hint[]): Hint[] {
  const taken = new Set(extra.map((h) => h.keys));
  return [...extra, ...GLOBAL_HINTS.filter((h) => !taken.has(h.keys))];
}

/** Screens call this to add their own hints while mounted. */
export function useShortcutHint(hints: Hint[]) {
  // Serialised so a fresh array literal on every render does not re-fire.
  const key = hints.map((h) => `${h.keys}:${h.label}`).join('|');
  useEffect(() => {
    publishHints(hints);
    return () => publishHints([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
}

function useOnline() {
  const [online, setOnline] = useState(() => navigator.onLine);
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
    };
  }, []);
  return online;
}
